import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { Send, Search, Phone, Video, Info, Image as ImageIcon, Smile, Paperclip, Mic, ChevronLeft, MessageCircle, Users, MoreVertical, AlertCircle, FileText, X, Download, Check, CheckCheck, Play, Pause } from 'lucide-react';
import io from 'socket.io-client';
import { ThemeContext } from './theme';
import { LanguageContext } from './LanguageContext';
import { useMessenger } from './MessengerContext';
import { apiFetch } from "../context/AuthContext";
import { useProfile } from "../hooks/useProfile";
const VoiceMessagePlayer = ({ audioUrl, duration, sent, c }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const audioRef = useRef(null);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);
const getDisplayName = (user, language) => {
  if (!user) return 'User';

  return language === 'ar'
    ? user.arabicName || user.sr || user.name
    : user.sr || user.name;
};

// Memoized message item to avoid re-rendering entire list
const MessageItem = React.memo(({ message, selectedUser, c, isRTL, renderMessageContent, employeeProfilePics }) => {
  const wrapperStyle = {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '10px',
    marginBottom: '16px',
    width: '100%',
    justifyContent: message.sent ? (isRTL ? 'flex-start' : 'flex-end') : (isRTL ? 'flex-end' : 'flex-start')
  };

  return (
    <div style={wrapperStyle} key={message.id}>
      {!message.sent && (
        <>
          {employeeProfilePics?.[selectedUser?.id] ? (
            <img src={employeeProfilePics[selectedUser.id]} alt="avatar" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} loading="lazy" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
          ) : null}
          <div style={{ width: 36, height: 36, borderRadius: '50%', display: employeeProfilePics?.[selectedUser?.id] ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', background: '#666', color: '#fff' }}>{selectedUser?.avatar}</div>
        </>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: message.sent ? 'flex-end' : 'flex-start', flex: 1, maxWidth: '100%' }}>
        <div style={{ padding: '12px 16px', borderRadius: '16px', fontSize: '15px', maxWidth: '70%', wordBreak: 'break-word', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', background: message.sent ? c.myMessageBg : c.messageBg, color: message.sent ? c.textOnPrimary : c.text }}>
          {renderMessageContent(message)}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px' }}>
          <p style={{ fontSize: 11, color: c.textSecondary, margin: 0 }}>{message.time}</p>
          {message.sent && message.status === 'sending' && <span style={{ fontSize: 10, color: c.textSecondary, fontStyle: 'italic' }}>• Sending...</span>}
        </div>
      </div>
    </div>
  );
}, (prev, next) => prev.message.id === next.message.id && prev.message.status === next.message.status && prev.message.time === next.message.time);


  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const changeSpeed = () => {
    const speeds = [1, 1.5, 2];
    const currentIndex = speeds.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % speeds.length;
    setPlaybackRate(speeds[nextIndex]);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px',
      background: sent ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)',
      borderRadius: '12px',
      minWidth: '240px',
    }}>
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
        onEnded={() => setIsPlaying(false)}
        onLoadedMetadata={(e) => {
          if (!duration) {
            duration = e.target.duration;
          }
        }}
      />

      <button
        onClick={togglePlay}
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          border: 'none',
          background: sent ? '#fff' : '#2563eb',
          color: sent ? '#2563eb' : '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: '18px',
        }}
      >
        {isPlaying ? '⏸' : '▶'}
      </button>

      <div style={{ flex: 1 }}>
        <div style={{
          width: '100%',
          height: '4px',
          background: 'rgba(0,0,0,0.2)',
          borderRadius: '2px',
          overflow: 'hidden',
        }}>
          <div style={{
            width: `${(currentTime / (audioRef.current?.duration || duration || 1)) * 100}%`,
            height: '100%',
            background: sent ? '#fff' : '#2563eb',
            transition: 'width 0.1s linear',
          }} />
        </div>
        <div style={{
          fontSize: '11px',
          marginTop: '4px',
          opacity: 0.9,
        }}>
          {formatTime(currentTime)} / {formatTime(audioRef.current?.duration || duration || 0)}
        </div>
      </div>

      <button
        onClick={changeSpeed}
        style={{
          padding: '6px 12px',
          borderRadius: '12px',
          border: 'none',
          background: 'rgba(0,0,0,0.1)',
          fontSize: '12px',
          fontWeight: '600',
          cursor: 'pointer',
        }}
      >
        {playbackRate}x
      </button>
    </div>
  );
};

const MessengerPage = () => {
  const { theme } = useContext(ThemeContext);
  const { profile } = useProfile();
  //const { language } = useContext(LanguageContext);
  //const isRTL = language === 'ar';

  // ✅ استخدم الـ Context بدل الـ state المحلي
  const {
    conversations,
    setConversations,
    messages,
    setMessages,
    onlineUsers,
    setOnlineUsers,
    currentUser,
    setCurrentUser,
    socket,
    setSocket,
    isConnected,
    setIsConnected,
    employeeProfilePics,
    setEmployeeProfilePics,
    selectedUser: contextSelectedUser,
    setSelectedUser: setContextSelectedUser,
  } = useMessenger();

  // بقية الـ state
  const [token, setToken] = useState(null);
  const [allEmployees, setAllEmployees] = useState([]);
  const [selectedUser, setSelectedUser] = useState(contextSelectedUser);
  // ... باقي الـ state  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const isRTL = language === 'ar';

  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordingTimerRef = useRef(null);
  // Keep full message lists per conversation for pagination
  const messagesFullRef = useRef({});
  const [messagesLoadedCount, setMessagesLoadedCount] = useState({});
  const [loadingMoreFor, setLoadingMoreFor] = useState(null);
  const objectUrlSetRef = useRef(new Set());
  const userAtBottomRef = useRef(true);
  const suppressAutoScrollRef = useRef(false);
  const forceScrollOnSelectRef = useRef(false);
  const loadMoreMessages = async (userId) => {
  // Prevent multiple simultaneous loads
  if (loadingMoreFor === userId) {
    console.log('⚠️ Already loading messages for this user');
    return;
  }
  
  if (!hasMoreMessages[userId]) {
    console.log('⚠️ No more messages to load');
    return;
  }

  const container = messagesContainerRef.current;
  if (!container) return;

  // Store current scroll position
  const oldScrollHeight = container.scrollHeight;
  const oldScrollTop = container.scrollTop;

  setLoadingMoreFor(userId);
  suppressAutoScrollRef.current = true;

  try {
    // Get the oldest message's timestamp
    const currentMessages = messages[userId] || [];
    if (currentMessages.length === 0) {
      console.log('⚠️ No messages to use as reference');
      setLoadingMoreFor(null);
      suppressAutoScrollRef.current = false;
      return;
    }

    const oldestMessage = currentMessages[0];
    const beforeTimestamp = oldestMessage?.raw?.created_at || oldestMessage?.raw?.time || oldestMessage?.raw?.createdAt;

    if (!beforeTimestamp) {
      console.log('⚠️ No timestamp found in oldest message');
      setLoadingMoreFor(null);
      suppressAutoScrollRef.current = false;
      return;
    }

    console.log(`📥 Loading more messages before:`, beforeTimestamp);

    const response = await apiFetch(
      `${API_BASE_URL}/api/private-messages/messages/${userId}?limit=50&before=${beforeTimestamp}`,
      { method: 'GET' }
    );

    if (!response.ok) throw new Error('Failed to fetch messages');
    const olderMessages = await response.json();

    const formattedMessages = (Array.isArray(olderMessages) ? olderMessages : (olderMessages.messages || olderMessages.data || [])).map(msg => ({
      id: msg.id,
      text: msg.content,
      type: msg.type || 'text',
      mediaUrl: msg.mediaUrl || msg.mediaData,
      thumbnailUrl: msg.thumbnailUrl || msg.thumbnailData,
      mediaType: msg.mediaType,
      mediaName: msg.mediaName,
      imageWidth: msg.imageWidth,
      imageHeight: msg.imageHeight,
      mediaSize: msg.mediaSize,
      duration: msg.duration,
      time: formatMessageTime(msg.created_at || msg.time || msg.createdAt),
      sent: isSameUser(msg.senderId, currentUser?.infinityCode),
      status: msg.status || 'sent',
      raw: msg,
    }));

    console.log(`✅ Loaded ${formattedMessages.length} older messages`);

    // Check if we got fewer messages than requested (means we reached the end)
    const hasMore = formattedMessages.length === 50;

    // Update state
    setHasMoreMessages(prev => ({ ...prev, [userId]: hasMore }));
    setMessagesOffset(prev => ({ ...prev, [userId]: (prev[userId] || 0) + formattedMessages.length }));
    
    // Prepend older messages to existing messages
    setMessages(prev => ({
      ...prev,
      [userId]: [...formattedMessages, ...(prev[userId] || [])]
    }));

    // Restore scroll position after render
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (container) {
          const newScrollHeight = container.scrollHeight;
          const scrollDiff = newScrollHeight - oldScrollHeight;
          container.scrollTop = oldScrollTop + scrollDiff;
          console.log(`📏 Restored scroll: old=${oldScrollTop}, new=${container.scrollTop}, diff=${scrollDiff}`);
        }
        suppressAutoScrollRef.current = false;
        setLoadingMoreFor(null);
      });
    });

  } catch (err) {
    console.error('❌ Failed to load more messages:', err);
    suppressAutoScrollRef.current = false;
    setLoadingMoreFor(null);
    alert('Failed to load older messages. Please try again.');
  }
};


  const colors = useMemo(() => ({
    light: {
      mainBg: "#f1f1f1ff",
      sidebarBg: "rgba(255, 255, 255, 0.95)",
      chatBg: "#f9fafb",
      messageBg: "#e5e7eb",
      myMessageBg: "#2563eb",
      inputBg: "#ffffff",
      hoverBg: "rgba(59, 130, 246, 0.08)",
      text: "#0f172a",
      textSecondary: "#64748b",
      textOnPrimary: "#ffffff",
      border: "rgba(0, 0, 0, 0.08)",
      inputBorder: "rgba(0, 0, 0, 0.12)",
      shadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
      glassEffect: "blur(16px)",
      online: "#22c55e",
      offline: "#94a3b8",
    },
    dark: {
      mainBg: "rgba(15, 23, 42, 0.95)",
      sidebarBg: "rgba(15, 23, 42, 0.95)",
      chatBg: "rgba(30, 41, 59, 0.5)",
      messageBg: "rgba(71, 85, 105, 0.5)",
      myMessageBg: "#2563eb",
      inputBg: "rgba(51, 65, 85, 0.8)",
      hoverBg: "rgba(255, 255, 255, 0.08)",
      text: "#ffffff",
      textSecondary: "rgba(255, 255, 255, 0.7)",
      textOnPrimary: "#ffffff",
      border: "rgba(255, 255, 255, 0.08)",
      inputBorder: "rgba(255, 255, 255, 0.12)",
      shadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
      glassEffect: "blur(16px)",
      online: "#22c55e",
      offline: "#64748b",
    }
  }), []);

  const c = colors[theme];

  const translations = {
    en: {
      messages: "Messages",
      searchPeople: "Search people...",
      recentChats: "Recent Chats",
      allEmployees: "All Employees",
      noResults: "No results found",
      tryDifferent: "Try searching with a different keyword",
      noEmployees: "No employees available",
      selectPerson: "Select a person to start messaging",
      yourMessages: "Your Messages",
      activeNow: "Active now",
      offline: "Offline",
      typing: "typing...",
      sending: "Sending...",
      justNow: "Just now",
      startConversation: "Start a conversation",
      loading: "Loading messages...",
      errorTitle: "Error",
      retry: "Retry",
      typeMessage: "Type a message...",
      cannotMessageSelf: "Cannot send messages to yourself",
      userInfoMissing: "User information missing",
      sendFailed: "Failed to send message. Please try again.",
      uploading: "Uploading...",
      file: "File",
      download: "Download",
      recording: "Recording...",
      voiceMessage: "Voice Message",
      stop: "Stop",
      send: "Send",
      cancel: "Cancel",
      microphoneAccess: "Please allow microphone access",
    },
    ar: {
      messages: "الرسائل",
      searchPeople: "ابحث عن أشخاص...",
      recentChats: "المحادثات الأخيرة",
      allEmployees: "جميع الموظفين",
      noResults: "لا توجد نتائج",
      tryDifferent: "جرب البحث بكلمة مختلفة",
      noEmployees: "لا يوجد موظفون متاحون",
      selectPerson: "اختر شخصًا لبدء المحادثة",
      yourMessages: "رسائلك",
      activeNow: "نشط الآن",
      offline: "غير متصل",
      typing: "يكتب...",
      sending: "جاري الإرسال...",
      justNow: "الآن",
      startConversation: "ابدأ محادثة",
      loading: "جاري تحميل الرسائل...",
      errorTitle: "خطأ",
      retry: "إعادة المحاولة",
      typeMessage: "اكتب رسالة...",
      cannotMessageSelf: "لا يمكنك إرسال رسالة لنفسك",
      userInfoMissing: "معلومات المستخدم غير متوفرة",
      sendFailed: "فشل إرسال الرسالة. يرجى المحاولة مرة أخرى.",
      uploading: "جاري الرفع...",
      file: "ملف",
      download: "تحميل",
      recording: "جاري التسجيل...",
      voiceMessage: "رسالة صوتية",
      stop: "إيقاف",
      send: "إرسال",
      cancel: "إلغاء",
      microphoneAccess: "يرجى السماح بالوصول للمايكروفون",
    }
  };

  const t = translations[language];

  const API_BASE_URL = process.env.REACT_APP_BASE_URL||"https://nexploy-eg.com";
  const SOCKET_URL = process.env.REACT_APP_BASE_SERVER_URL||"https://nexploy-eg.com";

  //const [currentUser, setCurrentUser] = useState(null);
 // const [token, setToken] = useState(null);
  //const [conversations, setConversations] = useState([]);
//  const [allEmployees, setAllEmployees] = useState([]);
  //const [selectedUser, setSelectedUser] = useState(null);
  //const [messages, setMessages] = useState({});
  const [messageInput, setMessageInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  //const [socket, setSocket] = useState(null);
 // const [isConnected, setIsConnected] = useState(false);
  //const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [typingUsers, setTypingUsers] = useState(new Set());
  const [userNamesCache, setUserNamesCache] = useState({});
  const [uploading, setUploading] = useState(false);
  const picCacheRef = useRef(new Map());
  //const [employeeProfilePics, setEmployeeProfilePics] = useState({});
const [loadingPics, setLoadingPics] = useState(new Set());

  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);

  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    isMobile: window.innerWidth <= 768,
    isTablet: window.innerWidth > 768 && window.innerWidth <= 1024,
  });
const [hasMoreMessages, setHasMoreMessages] = useState({});
const [messagesOffset, setMessagesOffset] = useState({});
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenSize({
        width,
        isMobile: width <= 768,
        isTablet: width > 768 && width <= 1024,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { isMobile } = screenSize;

  const isSameUser = (id1, id2) => {
    if (!id1 || !id2) return false;
    const num1 = parseInt(String(id1).trim());
    const num2 = parseInt(String(id2).trim());
    if (isNaN(num1) || isNaN(num2)) return false;
    return num1 === num2;
  };

  const extractInfinityCode = (data) => {
    if (!data || typeof data !== 'object') return null;

    const possibleFields = [
      'infinityCode', 'InfinityCode', 'Infinity Code', 'INFINITY_CODE',
      'infinity_code', 'infinity-code', 'infinitycode', 'INFINITYCODE',
      'id', 'ID', 'Id', '_id',
      'userId', 'user_id', 'USER_ID', 'UserId', 'userid', 'USERID',
      'employeeId', 'employee_id', 'EMPLOYEE_ID', 'EmployeeId', 'employeeid',
      'code', 'Code', 'CODE',
      'empCode', 'emp_code', 'EmpCode', 'empcode',
      'userCode', 'user_code', 'UserCode', 'usercode',
    ];

    for (const field of possibleFields) {
      if (data[field] !== undefined && data[field] !== null) {
        const value = String(data[field]).trim();
        const numValue = parseInt(value);
        if (!isNaN(numValue) && numValue > 0) {
          return numValue;
        }
      }
    }

    const nestedKeys = ['user', 'employee', 'data', 'profile', 'info'];
    for (const nestedKey of nestedKeys) {
      if (data[nestedKey] && typeof data[nestedKey] === 'object') {
        const nested = extractInfinityCode(data[nestedKey]);
        if (nested) return nested;
      }
    }

    return null;
  };

 const extractUserName = (data, language = 'en') => {
  if (!data) return 'You';

  // Arabic first if language is Arabic
  if (language === 'ar' && data.arabicName && data.arabicName.trim()) {
    return data.arabicName.trim();
  }

  // English fields
  const nameFields = [
    'sr', 'SR', 'SR.', 'Sr', 'sr.',
    'name', 'Name', 'NAME',
    'fullName', 'full_name', 'FullName', 'FULL_NAME',
    'username', 'userName', 'UserName', 'USERNAME',
  ];

  for (const field of nameFields) {
    if (data[field] && typeof data[field] === 'string' && data[field].trim()) {
      return data[field].trim();
    }
  }

  if (data.user) return extractUserName(data.user, language);
  if (data.employee) return extractUserName(data.employee, language);

  return 'You';
};


  const extractUserEmail = (data) => {
    if (!data) return '';

    const emailFields = ['email', 'Email', 'EMAIL', 'mail', 'userEmail'];

    for (const field of emailFields) {
      if (data[field] && typeof data[field] === 'string' && data[field].includes('@')) {
        return data[field].trim();
      }
    }

    return '';
  };

  const getUserInfoFromLocalStorage = () => {
    // Prefer profile from /users/me over localStorage
    const infinityCode = profile?.infinityCode || localStorage.getItem('infinityCode');
    const employeeName = profile?.employeeName || profile?.employeeArabicName || localStorage.getItem('employeeName');
    const email = profile?.email || localStorage.getItem('email') || localStorage.getItem('employeeEmail');

    if (infinityCode) {
      return {
        infinityCode: String(infinityCode),
        name: employeeName || 'Unknown',
        sr: employeeName || 'Unknown',
        email: email || '',
      };
    }

    const possibleStorageKeys = [
      'userInfo', 'user', 'currentUser', 'authUser',
      'employeeInfo', 'employee', 'currentEmployee',
    ];

    for (const key of possibleStorageKeys) {
      const data = localStorage.getItem(key);
      if (data) {
        try {
          const parsed = JSON.parse(data);
          const infinityCodeFromObj = extractInfinityCode(parsed);
          if (infinityCodeFromObj) {
            return parsed;
          }
        } catch (e) {
          continue;
        }
      }
    }

    return null;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ').filter(p => p.length > 0);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0] ? parts[0][0].toUpperCase() : '?';
  };

  // Function to get trilateral name (first 3 parts only)
  const getTrilateralName = (name) => {
    if (!name) return '';
    const parts = name.trim().split(' ').filter(p => p.length > 0);
    if (parts.length >= 3) {
      return `${parts[0]} ${parts[1]} ${parts[2]}`;
    }
    return name.trim();
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return t.justNow;
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return date.toLocaleDateString();
  };

  const formatMessageTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getAvatarColor = (name) => {
    const colors = ['#3b82f6', '#a855f7', '#ec4899', '#10b981', '#f97316', '#ef4444'];
    const index = (name || '').charCodeAt(0) % colors.length;
    return colors[index];
  };

  useEffect(() => {
    initializeMessaging();
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, []);
  // ✅ جلب صور المحادثات
useEffect(() => {
  const fetchAllProfilePics = async () => {
    if (!token || conversations.length === 0) return;
    
    const missingPics = conversations
      .filter(conv => !employeeProfilePics[conv.id])
      .map(conv => conv.id);
    
    if (missingPics.length === 0) return;
    
    const BATCH_SIZE = 5;
    
    for (let i = 0; i < missingPics.length; i += BATCH_SIZE) {
      const batch = missingPics.slice(i, i + BATCH_SIZE);
      
      await Promise.all(
        batch.map(id => fetchEmployeeProfilePic(id))
      );
      
      if (i + BATCH_SIZE < missingPics.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
  };
  
  fetchAllProfilePics();
}, [conversations, token]);

// ✅ تنظيف الذاكرة
useEffect(() => {
  return () => {
    Object.values(employeeProfilePics).forEach(url => {
      try { if (url) URL.revokeObjectURL(url); } catch (e) {}
    });

    picCacheRef.current.forEach(url => {
      try { URL.revokeObjectURL(url); } catch (e) {}
    });

    picCacheRef.current.clear();

    // Revoke any object URLs created for media previews to avoid memory leaks
    if (objectUrlSetRef && objectUrlSetRef.current) {
      objectUrlSetRef.current.forEach(url => {
        try { URL.revokeObjectURL(url); } catch (e) {}
      });
      objectUrlSetRef.current.clear();
    }
  };
}, []);
  // ✅ Function لجلب صور الموظفين
const fetchEmployeeProfilePic = async (infinityCode) => {
  try {
    // Check if already loaded in state
    if (employeeProfilePics[infinityCode]) {
      return employeeProfilePics[infinityCode];
    }

    // Check cache
    if (picCacheRef.current.has(infinityCode)) {
      const cachedUrl = picCacheRef.current.get(infinityCode);
      // Update state if not already there
      setEmployeeProfilePics(prev => ({
        ...prev,
        [infinityCode]: cachedUrl,
      }));
      return cachedUrl;
    }

    if (loadingPics.has(infinityCode)) {
      return null;
    }

    setLoadingPics(prev => new Set([...prev, infinityCode]));

    const response = await apiFetch(`${API_BASE_URL}/employees/pic/${infinityCode}`, { method: 'GET' });
    if (response.ok) {
      const blob = await response.blob();
      if (blob && blob.size > 0) {
        const url = URL.createObjectURL(blob);
      picCacheRef.current.set(infinityCode, url);
      
      // ✅ Update state so UI re-renders
      setEmployeeProfilePics(prev => ({
        ...prev,
        [infinityCode]: url,
      }));
      
      setLoadingPics(prev => {
        const newSet = new Set(prev);
        newSet.delete(infinityCode);
        return newSet;
      });
      
      return url;
      }
    }
    
    setLoadingPics(prev => {
      const newSet = new Set(prev);
      newSet.delete(infinityCode);
      return newSet;
    });
    
    return null;
  } catch (err) {
    if (err.response?.status !== 404) {
      console.error(`❌ Error loading pic for ${infinityCode}:`, err.message);
    }
    
    setLoadingPics(prev => {
      const newSet = new Set(prev);
      newSet.delete(infinityCode);
      return newSet;
    });
    
    return null;
  }
};

  const initializeMessaging = async () => {
    try {
      const authToken = localStorage.getItem('token');
      if (!authToken) {
        setError('Authentication required. Please log in.');
        return;
      }
      setToken(authToken);

      const userInfo = getUserInfoFromLocalStorage();

      if (!userInfo) {
        setError('User identification failed. Please re-login.');
        return;
      }

      const infinityCode = extractInfinityCode(userInfo);

      if (!infinityCode) {
        setError('User identification failed. Please re-login.');
        return;
      }

      const userName = extractUserName(userInfo, language);
      const userNameEn = extractUserName(userInfo, 'en');
      const userEmail = extractUserEmail(userInfo);

      const user = {
        id: infinityCode,
        infinityCode: infinityCode,
        name: userName,
        email: userEmail,
        avatar: getInitials(userNameEn),
      };

      setCurrentUser(user);

      await initializeSocket(authToken, user);
      await new Promise(resolve => setTimeout(resolve, 1000));

      await Promise.all([
        fetchAllEmployees(authToken, user),
        fetchConversations(authToken, user),
      ]);

    } catch (err) {
      setError(`Failed to initialize messaging: ${err.message}`);
    }
  };

  const initializeSocket = (authToken, user) => {
    return new Promise((resolve, reject) => {
      const socketInstance = io(SOCKET_URL, {
        auth: { token: authToken },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 3000,
      });

      socketInstance.on('connect', () => {
        setIsConnected(true);
        setSocket(socketInstance);
        resolve(socketInstance);
      });

      socketInstance.on('disconnect', () => {
        setIsConnected(false);
      });

      socketInstance.on('connect_error', (error) => {
        setError('Connection failed. Retrying...');
        reject(error);
      });

      socketInstance.on('private_message:received', (data) => {
        handleIncomingMessage(data, user);
      });

      socketInstance.on('private_message:typing', (data) => {
        if (data.isTyping) {
          setTypingUsers(prev => new Set(prev).add(data.senderId));
        } else {
          setTypingUsers(prev => {
            const newSet = new Set(prev);
            newSet.delete(data.senderId);
            return newSet;
          });
        }
      });

      socketInstance.on('private_messages:read', (data) => {
        markMessagesAsRead(data.userId);
      });

      socketInstance.on('users:online', (data) => {
        if (Array.isArray(data)) {
          setOnlineUsers(new Set(data));
        }
      });

      socketInstance.on('user:online', (data) => {
        setOnlineUsers(prev => new Set(prev).add(data.userId));
      });

      socketInstance.on('user:offline', (data) => {
        setOnlineUsers(prev => {
          const newSet = new Set(prev);
          newSet.delete(data.userId);
          return newSet;
        });
      });

      setSocket(socketInstance);
    });
  };

  const fetchAllEmployees = async (authToken, user) => {
    try {
      const clientName = profile?.clientName || localStorage.getItem('clientName') || 'all';
      const endpoint = clientName.toLowerCase() === 'infinity' || clientName === 'all'
        ? '/employees'
        : `/employees/clients/${clientName}`;

      const response = await apiFetch(`${API_BASE_URL}${endpoint}`, { method: 'GET' });
      if (!response.ok) throw new Error('Failed to fetch employees');
      const data = await response.json();
      const employeesData = data.employees || data;

      const employees = employeesData
        .map(emp => {
          const empInfinityCode = extractInfinityCode(emp);
          if (!empInfinityCode) return null;

          const empName = extractUserName(emp, language);
          const empNameEn = extractUserName(emp, 'en');
          const empEmail = extractUserEmail(emp);

          return {
            id: empInfinityCode,
            infinityCode: empInfinityCode,
            name: getTrilateralName(empName),
            email: empEmail,
            avatar: getInitials(empNameEn),
            department: emp.location || emp['Location'] || '',
            position: emp.employeeType || emp['Employee Type'] || '',
          };
        })
        .filter(emp => {
          if (!emp) return false;
          return !isSameUser(emp.id, user?.infinityCode);
        });

      setAllEmployees(employees);

      const namesCache = {};
      employees.forEach(emp => {
        namesCache[emp.id] = emp.name;
      });
      setUserNamesCache(namesCache);

    } catch (err) {
      setError('Failed to load employees');
    }
  };

  const fetchConversations = async (authToken, user) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch(`${API_BASE_URL}/api/private-messages/conversations`, { method: 'GET' });

      if (!response.ok) throw new Error('Failed to fetch conversations');

      const data = await response.json();
      let conversationsArray = Array.isArray(data) ? data : (data.conversations || data.data || []);

      if (conversationsArray.length === 0) {
        setConversations([]);
        return;
      }

      const mappedConversations = conversationsArray
        .map((conv) => {
          const otherUser = conv.user || conv.otherUser || {};

          const otherUserInfinityCode = extractInfinityCode(otherUser);
          const otherUserId =
            otherUserInfinityCode !== null && otherUserInfinityCode !== undefined
              ? String(otherUserInfinityCode)
              : null;

          if (!otherUserId) {
            return null;
          }

          if (isSameUser(otherUserId, user?.infinityCode)) {
            return null;
          }

   const userName =
  language === 'ar'
    ? otherUser.arabicName || otherUser.sr || otherUser.name || userNamesCache[otherUserId]
    : otherUser.sr || otherUser.name || userNamesCache[otherUserId]
    || `User ${otherUserId}`;

   const userNameEn = otherUser.sr || otherUser.name || userNamesCache[otherUserId] || `User ${otherUserId}`;

          return {
            id: otherUserId,
            name: getTrilateralName(userName),
            avatar: getInitials(userNameEn),
            lastMessage: conv.lastMessage || conv.lastMessageContent || '',
            time: formatTime(conv.lastMessageTime || new Date()),
            timestamp: new Date(conv.lastMessageTime || new Date()).getTime(),
            unread: conv.unreadCount || 0,
            online: onlineUsers.has(otherUserId),
          };
        })
        .filter(conv => conv && conv.id && !isNaN(conv.id));

      mappedConversations.sort((a, b) => b.timestamp - a.timestamp);
      setConversations(mappedConversations);

    } catch (err) {
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

const fetchMessagesForUser = async (userId) => {
  try {
    if (isSameUser(userId, currentUser?.infinityCode)) return;

    console.log(`📥 Fetching initial messages for user ${userId}`);

    const response = await apiFetch(`${API_BASE_URL}/api/private-messages/messages/${userId}?limit=100`, { method: 'GET' });

    if (!response.ok) throw new Error('Failed to fetch messages');
    const messagesData = await response.json();

    const formattedMessages = (Array.isArray(messagesData) ? messagesData : (messagesData.messages || messagesData.data || [])).map(msg => ({
      id: msg.id,
      text: msg.content,
      type: msg.type || 'text',
      mediaUrl: msg.mediaUrl || msg.mediaData,
      thumbnailUrl: msg.thumbnailUrl || msg.thumbnailData,
      mediaType: msg.mediaType,
      mediaName: msg.mediaName,
      imageWidth: msg.imageWidth,
      imageHeight: msg.imageHeight,
      mediaSize: msg.mediaSize,
      duration: msg.duration,
      time: formatMessageTime(msg.created_at || msg.time || msg.createdAt),
      sent: isSameUser(msg.senderId, currentUser?.infinityCode),
      status: msg.status || 'sent',
      raw: msg,
    }));

    // Set messages
    setMessages(prev => ({ ...prev, [userId]: formattedMessages }));
    
    // Track if there are more messages
    setHasMoreMessages(prev => ({ ...prev, [userId]: formattedMessages.length === 100 }));
    
    // Track current offset
    setMessagesOffset(prev => ({ ...prev, [userId]: formattedMessages.length }));

    console.log(`✅ Loaded ${formattedMessages.length} messages`);

    // Mark as read if needed
    if (socket && formattedMessages.some(m => !m.sent)) {
      socket.emit('private_message:mark_read', { senderId: userId });
    }

    // Force scroll to bottom on first load
    forceScrollOnSelectRef.current = true;

  } catch (err) {
    console.error('Failed to fetch messages:', err);
  }
};


  const startNewConversation = async (employee) => {
    if (!employee.id || !currentUser?.infinityCode) {
      alert(t.userInfoMissing);
      return;
    }

    if (isSameUser(employee.id, currentUser?.infinityCode)) {
      alert(t.cannotMessageSelf);
      return;
    }

    const existingConv = conversations.find(conv => isSameUser(conv.id, employee.id));
    if (existingConv) {
      handleUserSelect(existingConv);
      return;
    }

    setSelectedUser({
      id: employee.id,
      name: getTrilateralName(employee.name),
      avatar: employee.avatar,
      online: onlineUsers.has(employee.id),
    });

    // Fetch profile pic if not already loaded
    if (!employeeProfilePics[employee.id]) {
      await fetchEmployeeProfilePic(employee.id);
    }

    setMessages(prev => ({ ...prev, [employee.id]: [] }));
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      const audioChunks = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunks.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunks, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      setRecordingTime(0);

      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (err) {
      console.error('Microphone access denied:', err);
      alert(t.microphoneAccess);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingTimerRef.current);
    }
  };

  const cancelRecording = () => {
    stopRecording();
    setAudioBlob(null);
    setRecordingTime(0);
  };

  const sendRecording = async () => {
    if (!audioBlob || !selectedUser) return;

    try {
      setUploading(true);

      const voiceFile = new File([audioBlob], `voice_${Date.now()}.webm`, { type: 'audio/webm' });
      const formData = new FormData();
      formData.append('audio', voiceFile);

      const uploadRes = await apiFetch(`${API_BASE_URL}/api/private-messages/upload/audio`, {
        method: 'POST',
        body: formData,
      });

      if (!uploadRes.ok) {
        const err = await uploadRes.json().catch(() => ({}));
        throw new Error(err.error || 'Voice upload failed');
      }

      const { data: uploaded } = await uploadRes.json();

      const mediaData = {
        storageKey: uploaded.storageKey,
        url: uploaded.url,
        type: 'voice',
        name: uploaded.name || voiceFile.name,
        size: uploaded.size || audioBlob.size,
        duration: recordingTime,
        mimeType: 'audio/webm',
      };

      await sendMediaMessage(mediaData, 'voice');

      setAudioBlob(null);
      setRecordingTime(0);

    } catch (err) {
      console.error('Failed to send voice:', err);
      alert(t.sendFailed);
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!selectedUser) {
      alert(t.userInfoMissing);
      return;
    }

    const fileTypeMap = {
      'image/jpeg': { type: 'image', icon: '🖼️', maxSize: 5 },
      'image/jpg': { type: 'image', icon: '🖼️', maxSize: 5 },
      'image/png': { type: 'image', icon: '🖼️', maxSize: 5 },
      'image/gif': { type: 'image', icon: '🖼️', maxSize: 5 },
      'image/webp': { type: 'image', icon: '🖼️', maxSize: 5 },
      'application/pdf': { type: 'pdf', icon: '📄', maxSize: 10 },
      'application/vnd.ms-excel': { type: 'excel', icon: '📊', maxSize: 5 },
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { type: 'excel', icon: '📊', maxSize: 5 },
      'application/msword': { type: 'word', icon: '📝', maxSize: 5 },
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { type: 'word', icon: '📝', maxSize: 5 },
      'application/vnd.ms-powerpoint': { type: 'powerpoint', icon: '📽️', maxSize: 5 },
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': { type: 'powerpoint', icon: '📽️', maxSize: 5 },
      'audio/webm': { type: 'voice', icon: '🎤', maxSize: 5 },
      'audio/mp4': { type: 'voice', icon: '🎤', maxSize: 5 },
      'audio/mpeg': { type: 'voice', icon: '🎤', maxSize: 5 },
    };

    const fileInfo = fileTypeMap[file.type];

    if (!fileInfo) {
      alert(`Unsupported file type: ${file.type}`);
      return;
    }

    const maxSizeMB = fileInfo.maxSize;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    if (file.size > maxSizeBytes) {
      alert(`File size must be less than ${maxSizeMB}MB`);
      return;
    }

    try {
      setUploading(true);

      const previewUrl = URL.createObjectURL(file);
      objectUrlSetRef.current.add(previewUrl);

      const isImage = fileInfo.type === 'image';
      const isVoice = fileInfo.type === 'voice';
      const endpoint = isImage ? 'upload/image' : isVoice ? 'upload/audio' : 'upload/pdf';
      const formData = new FormData();
      formData.append(isImage ? 'image' : isVoice ? 'audio' : 'pdf', file);

      const uploadRes = await apiFetch(`${API_BASE_URL}/api/private-messages/${endpoint}`, {
        method: 'POST',
        body: formData,
      });

      if (!uploadRes.ok) {
        const err = await uploadRes.json().catch(() => ({}));
        throw new Error(err.error || err.message || 'Upload failed');
      }

      const { data: uploaded } = await uploadRes.json();

      let width = uploaded.width;
      let height = uploaded.height;

      if (isImage && (!width || !height)) {
        const img = new Image();
        await new Promise((resolve) => {
          img.onload = resolve;
          img.src = previewUrl;
        });
        width = img.width;
        height = img.height;
      }

      const mediaData = {
        storageKey: uploaded.storageKey,
        thumbnailKey: uploaded.thumbnailKey || null,
        url: uploaded.url,
        thumbnailUrl: uploaded.thumbnailUrl || null,
        previewUrl,
        type: fileInfo.type,
        name: file.name,
        size: uploaded.size || file.size,
        width,
        height,
        mimeType: file.type,
        icon: fileInfo.icon,
      };

      await sendMediaMessage(mediaData, fileInfo.type);

    } catch (err) {
      alert(`Failed to upload file: ${err.message}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleUserSelect = async (user) => {
    setSelectedUser({
      id: user.id,
      name: getTrilateralName(user.name),
      avatar: user.avatar,
      online: user.online,
    });

    // Fetch profile pic if not already loaded
    if (!employeeProfilePics[user.id]) {
      await fetchEmployeeProfilePic(user.id);
    }

    if (!messages[user.id] || messages[user.id].length === 0) {
      await fetchMessagesForUser(user.id);
      // after fetching messages, force scroll to bottom for first load
      forceScrollOnSelectRef.current = true;
    }

    // if messages exist but full cache not populated, initialize cache for pagination
    if ((messages[user.id] && messages[user.id].length > 0) && (!messagesFullRef.current[user.id] || messagesFullRef.current[user.id].length === 0)) {
      try {
        const full = messages[user.id].slice();
        messagesFullRef.current[user.id] = full;
        const total = full.length;
        const initial = Math.min(100, total);
        setMessagesLoadedCount(prev => ({ ...prev, [user.id]: initial }));
        setMessages(prev => ({ ...prev, [user.id]: full.slice(-initial) }));
      } catch (e) {}
    }

    setConversations(prev => prev.map(c =>
      isSameUser(c.id, user.id) ? { ...c, unread: 0 } : c
    ));
  };


 const handleIncomingMessage = (data, user = currentUser) => {
  const normalizeParticipantId = (value) => {
    if (value === undefined || value === null) return null;
    const parsed = parseInt(String(value).trim(), 10);
    return Number.isFinite(parsed) && parsed > 0 ? String(parsed) : null;
  };

  const senderId = normalizeParticipantId(
    data?.sender?.infinityCode ||
    data?.senderInfinityCode ||
    data?.senderCode ||
    data?.senderId
  );

  const receiverId = normalizeParticipantId(
    data?.receiver?.infinityCode ||
    data?.receiverInfinityCode ||
    data?.recipientId ||
    data?.receiverId
  );

  const myId = normalizeParticipantId(user?.infinityCode);
  const selectedChatId = normalizeParticipantId(selectedUser?.id);
  const isSentByMe = !!(senderId && myId && senderId === myId);

  const chatPartnerId = isSentByMe
    ? (selectedChatId || receiverId)
    : senderId;

  if (!chatPartnerId) {
    return;
  }

  if (isSentByMe && receiverId && !isSameUser(receiverId, chatPartnerId)) {
    return;
  }

  if (isSameUser(chatPartnerId, user?.infinityCode)) {
    return;
  }

  const isDuplicate = messages[chatPartnerId]?.some(m => m.id === data.id);
  if (isDuplicate) return;

  const newMessage = {
    id: data.id || Date.now(),
    text: data.content,
    type: data.type || 'text',
    mediaUrl: data.mediaUrl || data.mediaData,
    thumbnailUrl: data.thumbnailUrl || data.thumbnailData,
    mediaType: data.mediaType,
    mediaName: data.mediaName,
    imageWidth: data.imageWidth,
    imageHeight: data.imageHeight,
    mediaSize: data.mediaSize,
    duration: data.duration,
    time: formatMessageTime(data.created_at || new Date().toISOString()),
    sent: isSentByMe,
    status: 'delivered',
    raw: {
      id: data.id,
      created_at: data.created_at || new Date().toISOString(),
      senderId,
      receiverId,
    }
  };

  // Append new message to existing messages
  setMessages(prev => ({
    ...prev,
    [chatPartnerId]: [...(prev[chatPartnerId] || []), newMessage],
  }));

  // Update offset
  setMessagesOffset(prev => ({
    ...prev,
    [chatPartnerId]: (prev[chatPartnerId] || 0) + 1
  }));

  // Update conversation with proper preview
  setConversations(prev => {
    const existingIndex = prev.findIndex(conv => isSameUser(conv.id, chatPartnerId));
    const now = Date.now();

    const knownEmployee = allEmployees.find(emp => isSameUser(emp.id, chatPartnerId));

    let userName = existingIndex >= 0
      ? prev[existingIndex].name
      : knownEmployee?.name ||
        userNamesCache[chatPartnerId] ||
        (!isSentByMe ? (data?.sender?.sr || data?.senderName) : null) ||
        (isSentByMe && selectedUser && isSameUser(selectedUser.id, chatPartnerId)
          ? selectedUser.name
          : null) ||
        `User ${chatPartnerId}`;

    let userNameEn = existingIndex >= 0
      ? (userNamesCache[chatPartnerId] || userName)
      : knownEmployee?.name ||
        userNamesCache[chatPartnerId] ||
        (!isSentByMe ? (data?.sender?.sr || data?.senderName) : null) ||
        (isSentByMe && selectedUser && isSameUser(selectedUser.id, chatPartnerId)
          ? selectedUser.name
          : null) ||
        `User ${chatPartnerId}`;

    // Determine preview text for conversation
    let previewText;
    if (data.content) {
      previewText = data.content;
    } else {
      switch (data.type) {
        case 'image': previewText = '📷 Image'; break;
        case 'voice': previewText = '🎤 Voice Message'; break;
        case 'pdf': previewText = '📄 PDF Document'; break;
        case 'excel': previewText = '📊 Excel File'; break;
        case 'word': previewText = '📝 Word Document'; break;
        case 'powerpoint': previewText = '📽️ PowerPoint'; break;
        default: previewText = 'Message';
      }
    }

    const updatedConv = {
      id: chatPartnerId,
      name: userName,
      avatar: existingIndex >= 0 ? prev[existingIndex].avatar : getInitials(userNameEn),
      lastMessage: previewText,
      time: formatTime(data.created_at || new Date().toISOString()),
      timestamp: now,
      unread: (isSameUser(selectedUser?.id, chatPartnerId) || isSentByMe) ? 0 :
        (existingIndex >= 0 ? prev[existingIndex].unread + 1 : 1),
      online: onlineUsers.has(chatPartnerId),
    };

    if (existingIndex >= 0) {
      const updated = [...prev];
      updated.splice(existingIndex, 1);
      return [updatedConv, ...updated];
    } else {
      return [updatedConv, ...prev];
    }
  });

  if (isSameUser(selectedUser?.id, chatPartnerId) && !isSentByMe && socket) {
    setTimeout(() => {
      socket.emit('private_message:mark_read', { senderId: chatPartnerId });
    }, 500);
  }
};


 const handleSendMessage = async () => {
  if (!messageInput.trim() || !selectedUser || !socket || !isConnected) return;

  if (!selectedUser.id || !currentUser?.infinityCode) {
    alert(t.userInfoMissing);
    return;
  }

  if (isSameUser(selectedUser.id, currentUser?.infinityCode)) {
    alert(t.cannotMessageSelf);
    setSelectedUser(null);
    return;
  }

  const messageContent = messageInput.trim();
  const receiverId = selectedUser.id;
  setMessageInput('');

  const tempId = `temp-${Date.now()}`;
  const now = new Date();

  const tempMessage = {
    id: tempId,
    text: messageContent,
    type: 'text',
    time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    sent: true,
    status: 'sending',
    raw: {
      id: tempId,
      created_at: now.toISOString(),
      senderId: currentUser.infinityCode,
      receiverId,
    }
  };
  
  setMessages(prev => ({
    ...prev,
    [receiverId]: [...(prev[receiverId] || []), tempMessage],
  }));

  // Update offset
  setMessagesOffset(prev => ({
    ...prev,
    [receiverId]: (prev[receiverId] || 0) + 1
  }));

  try {
    socket.emit('private_message:send', {
      receiverId: receiverId,
      content: messageContent,
      type: 'text',
    }, (response) => {
      if (response?.success) {
        setMessages(prev => ({
          ...prev,
          [receiverId]: prev[receiverId].map(msg =>
            msg.id === tempId
              ? { 
                  ...msg, 
                  id: response.data.id, 
                  status: 'sent',
                  raw: {
                    ...msg.raw,
                    id: response.data.id,
                  }
                }
              : msg
          ),
        }));
      } else {
        throw new Error(response?.error || 'Failed to send');
      }
    });

    setConversations(prev => {
      const existingIndex = prev.findIndex(conv => isSameUser(conv.id, receiverId));
      const timestamp = now.getTime();

      const updatedConv = {
        id: receiverId,
        name: selectedUser.name,
        avatar: selectedUser.avatar,
        lastMessage: messageContent,
        time: t.justNow,
        timestamp: timestamp,
        unread: 0,
        online: onlineUsers.has(receiverId),
      };

      if (existingIndex >= 0) {
        const updated = [...prev];
        updated.splice(existingIndex, 1);
        return [updatedConv, ...updated];
      } else {
        return [updatedConv, ...prev];
      }
    });

  } catch (err) {
    setMessages(prev => ({
      ...prev,
      [receiverId]: prev[receiverId].filter(msg => msg.id !== tempId),
    }));
    
    // Update offset
    setMessagesOffset(prev => ({
      ...prev,
      [receiverId]: Math.max(0, (prev[receiverId] || 0) - 1)
    }));
    
    alert(t.sendFailed);
  }
};

  const handleTyping = () => {
    if (!socket || !selectedUser) return;

    socket.emit('private_message:typing', {
      recipientId: selectedUser.id,
      isTyping: true,
    });

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      socket.emit('private_message:typing', {
        recipientId: selectedUser.id,
        isTyping: false,
      });
    }, 3000);
  };

  const markMessagesAsRead = (userId) => {
    setMessages(prev => ({
      ...prev,
      [userId]: (prev[userId] || []).map(msg => ({
        ...msg,
        status: msg.sent ? 'read' : msg.status,
      })),
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Replace your sendMediaMessage function with this fixed version:
  const sendMediaMessage = async (mediaData, mediaType) => {
    if (!selectedUser || !isConnected) return;

    const tempId = `temp-${Date.now()}`;
    const now = new Date();

    const tempMessage = {
      id: tempId,
      text: messageInput.trim() || null,
      type: mediaType,
      mediaUrl: mediaData.previewUrl || mediaData.url,
      thumbnailUrl: mediaData.thumbnailUrl || mediaData.previewUrl || mediaData.url,
      mediaType: mediaData.mimeType,
      mediaName: mediaData.name,
      imageWidth: mediaData.width,
      imageHeight: mediaData.height,
      mediaSize: mediaData.size,
      duration: mediaData.duration,
      time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      sent: true,
      status: 'sending',
    };

    setMessages(prev => ({
      ...prev,
      [selectedUser.id]: [...(prev[selectedUser.id] || []), tempMessage],
    }));

    // include temp media message in full cache for pagination
    try {
      const full = messagesFullRef.current[selectedUser.id] || [];
      full.push(tempMessage);
      messagesFullRef.current[selectedUser.id] = full;
    } catch (e) {}

    const caption = messageInput.trim();
    setMessageInput('');

    try {
      const response = await apiFetch(`${API_BASE_URL}/api/private-messages/messages/media`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipientId: selectedUser.id,
          storageKey: mediaData.storageKey || null,
          thumbnailKey: mediaData.thumbnailKey || null,
          mediaUrl: mediaData.url || null,
          mediaType: mediaType,
          thumbnailUrl: mediaData.thumbnailUrl || null,
          width: mediaData.width,
          height: mediaData.height,
          size: mediaData.size,
          name: mediaData.name,
          mimeType: mediaData.mimeType,
          duration: mediaData.duration,
          caption: caption || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to send media');
      }

      const result = await response.json();

      if (result.success) {
        // Update the temp message with real data from server
        setMessages(prev => ({
          ...prev,
          [selectedUser.id]: prev[selectedUser.id].map(msg =>
            msg.id === tempId
              ? {
                ...msg,
                id: result.data.id,
                status: 'sent',
                mediaUrl: result.data.mediaUrl || msg.mediaUrl,
                thumbnailUrl: result.data.thumbnailUrl || msg.thumbnailUrl,
              }
              : msg
          ),
        }));

        // revoke any preview object URL for this message (we prefer server URL)
        try {
          if (mediaData.previewUrl) {
            URL.revokeObjectURL(mediaData.previewUrl);
            objectUrlSetRef.current.delete(mediaData.previewUrl);
          }
        } catch (e) {}

        // Update the full cache to replace tempId with real id
        try {
          const full = messagesFullRef.current[selectedUser.id] || [];
          messagesFullRef.current[selectedUser.id] = full.map(msg =>
            msg.id === tempId ? { ...msg, id: result.data.id, status: 'sent', mediaUrl: result.data.mediaUrl, thumbnailUrl: result.data.thumbnailUrl } : msg
          );
        } catch (e) {}

        // Update conversation preview
        setConversations(prev => {
          const existingIndex = prev.findIndex(conv => isSameUser(conv.id, selectedUser.id));
          const timestamp = now.getTime();

          // Determine preview text
          let previewText;
          if (caption) {
            previewText = caption;
          } else {
            switch (mediaType) {
              case 'image': previewText = '📷 Image'; break;
              case 'voice': previewText = '🎤 Voice Message'; break;
              case 'pdf': previewText = '📄 PDF Document'; break;
              case 'excel': previewText = '📊 Excel File'; break;
              case 'word': previewText = '📝 Word Document'; break;
              case 'powerpoint': previewText = '📽️ PowerPoint'; break;
              default: previewText = 'File';
            }
          }

          const updatedConv = {
            id: selectedUser.id,
            name: selectedUser.name,
            avatar: selectedUser.avatar,
            lastMessage: previewText,
            time: t.justNow,
            timestamp: timestamp,
            unread: 0,
            online: onlineUsers.has(selectedUser.id),
          };

          if (existingIndex >= 0) {
            const updated = [...prev];
            updated.splice(existingIndex, 1);
            return [updatedConv, ...updated];
          } else {
            return [updatedConv, ...prev];
          }
        });
      }

    } catch (err) {
      console.error('Failed to send media:', err);

      // Remove the failed message
      setMessages(prev => ({
        ...prev,
        [selectedUser.id]: prev[selectedUser.id].filter(msg => msg.id !== tempId),
      }));

      alert(t.sendFailed + ': ' + err.message);
    }
  };

  const renderMessageContent = (message) => {
    if (message.type === 'image') {
      return (
        <div>
          <img
            src={message.mediaUrl}
            alt="Shared"
            style={{
              maxWidth: '300px',
              maxHeight: '400px',
              borderRadius: '12px',
              display: 'block',
              cursor: 'pointer',
            }}
            onClick={() => window.open(message.mediaUrl, '_blank')}
          />
          {message.text && (
            <p style={{ margin: '8px 0 0 0', wordBreak: 'break-word' }}>
              {message.text}
            </p>
          )}
        </div>
      );
    }

    if (message.type === 'voice') {
      return (
        <VoiceMessagePlayer
          audioUrl={message.mediaUrl}
          duration={message.duration}
          sent={message.sent}
          c={c}
        />
      );
    }

    if (message.type === 'pdf' || message.type === 'excel' || message.type === 'word' || message.type === 'powerpoint') {
      const typeColors = {
        pdf: '#ef4444',
        excel: '#10b981',
        word: '#3b82f6',
        powerpoint: '#f97316'
      };

      return (
        <div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px',
            background: message.sent ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)',
            borderRadius: '8px',
            cursor: 'pointer',
            minWidth: '220px',
          }}
            onClick={() => window.open(message.mediaUrl, '_blank')}
          >
            <FileText size={40} color={typeColors[message.type]} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px', wordBreak: 'break-word' }}>
                {message.mediaName || `Document.${message.type}`}
              </div>
              <div style={{ fontSize: '12px', opacity: 0.9 }}>
                {t.file} • {message.mediaSize ? `${(message.mediaSize / 1024).toFixed(0)} KB` : ''}
              </div>
            </div>
            <Download size={20} />
          </div>
          {message.text && (
            <p style={{ margin: '8px 0 0 0', wordBreak: 'break-word' }}>
              {message.text}
            </p>
          )}
        </div>
      );
    }

    return (
      <p style={{
        margin: 0,
        wordBreak: 'break-word',
        lineHeight: '1.5',
        whiteSpace: 'pre-wrap',
      }}>
        {message.text}
      </p>
    );
  };

  const getResponsiveStyles = () => ({
    container: {
      position: 'relative', // ✅ غيّر من fixed لـ relative
      width: '100%',
      height: '100%', // ✅ غيّر من fixed heights
      display: 'flex',
      background: c.mainBg,
      overflow: 'hidden',
      borderRadius: '20px',
      boxShadow: c.shadow,
      direction: isRTL ? 'rtl' : 'ltr',
      minHeight: 'calc(100vh - 200px)', // ✅ أضف min-height
      marginTop: -10,
    },
    sidebar: {
      display: selectedUser && isMobile ? 'none' : 'flex',
      width: isMobile ? '100%' : '380px',
      minWidth: isMobile ? 'auto' : '380px',
      background: c.sidebarBg,
      backdropFilter: c.glassEffect,
      borderRight: isRTL ? 'none' : `1px solid ${c.border}`,
      borderLeft: isRTL ? `1px solid ${c.border}` : 'none',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden',
    },
    sidebarHeader: {
      padding: isMobile ? '16px' : '20px',
      borderBottom: `1px solid ${c.border}`,
      flexShrink: 0,
    },
    sidebarTitle: {
      fontSize: isMobile ? '20px' : '22px',
      fontWeight: 'bold',
      color: c.text,
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '16px',
    },
    searchContainer: {
      position: 'relative',
    },
    searchInput: {
      width: '100%',
      paddingLeft: isRTL ? '16px' : '44px',
      paddingRight: isRTL ? '44px' : '16px',
      paddingTop: '12px',
      paddingBottom: '12px',
      background: c.inputBg,
      border: `1px solid ${c.inputBorder}`,
      borderRadius: '12px',
      outline: 'none',
      fontSize: '14px',
      color: c.text,
      transition: 'all 0.3s ease',
    },
    searchIcon: {
      position: 'absolute',
      left: isRTL ? 'auto' : '14px',
      right: isRTL ? '14px' : 'auto',
      top: '50%',
      transform: 'translateY(-50%)',
      color: c.textSecondary,
      width: '18px',
      height: '18px',
    },
    usersList: {
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
    },
    sectionTitle: {
      padding: '16px 20px 8px',
      fontSize: '11px',
      fontWeight: '700',
      color: c.textSecondary,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    conversationItem: {
      padding: '12px 16px',
      cursor: 'pointer',
      background: 'transparent',
      transition: 'all 0.2s ease',
      borderLeft: isRTL ? 'none' : '3px solid transparent',
      borderRight: isRTL ? '3px solid transparent' : 'none',
    },
    conversationItemActive: {
      background: c.hoverBg,
      borderLeftColor: isRTL ? 'transparent' : '#2563eb',
      borderRightColor: isRTL ? '#2563eb' : 'transparent',
    },
    avatarContainer: {
      position: 'relative',
      flexShrink: 0,
    },
    avatar: {
      width: '52px',
      height: '52px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#ffffff',
      fontWeight: '700',
      fontSize: '18px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      border: `3px solid ${c.sidebarBg}`,
      transition: 'all 0.2s ease',
    },
    onlineIndicator: {
      position: 'absolute',
      bottom: '2px',
      right: isRTL ? 'auto' : '2px',
      left: isRTL ? '2px' : 'auto',
      width: '16px',
      height: '16px',
      background: c.online,
      border: `3px solid ${c.sidebarBg}`,
      borderRadius: '50%',
      boxShadow: '0 2px 8px rgba(34, 197, 94, 0.4)',
    },
    chatArea: {
      display: (!selectedUser && !isMobile) ? 'none' : 'flex',
      flex: 1,
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden',
      width: '100%',
      background: c.chatBg,
    },
    chatHeader: {
      background: c.sidebarBg,
      backdropFilter: c.glassEffect,
      borderBottom: `1px solid ${c.border}`,
      padding: '16px 20px',
      flexShrink: 0,
    },
    backButton: {
      padding: '10px',
      background: 'transparent',
      border: 'none',
      borderRadius: '50%',
      cursor: 'pointer',
      display: isMobile ? 'flex' : 'none',
      alignItems: 'center',
      justifyContent: 'center',
      color: c.text,
      transition: 'all 0.2s ease',
    },
    messagesContainer: {
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
      padding: isMobile ? '16px' : '24px',
      background: c.chatBg,
    },
    messageWrapper: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: '10px',
      marginBottom: '16px',
      width: '100%',
    },
    messageBubble: {
      padding: '12px 16px',
      borderRadius: '16px',
      fontSize: '15px',
      maxWidth: isMobile ? '80%' : '70%',
      minWidth: '80px',
      wordBreak: 'break-word',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    },
    myMessage: {
      background: c.myMessageBg,
      color: c.textOnPrimary,
      borderBottomRightRadius: isRTL ? '16px' : '4px',
      borderBottomLeftRadius: isRTL ? '4px' : '16px',
      marginLeft: isRTL ? '0' : 'auto',
      marginRight: isRTL ? 'auto' : '0',
    },
    theirMessage: {
      background: c.messageBg,
      color: c.text,
      borderBottomRightRadius: isRTL ? '4px' : '16px',
      borderBottomLeftRadius: isRTL ? '16px' : '4px',
      marginRight: isRTL ? '0' : 'auto',
      marginLeft: isRTL ? 'auto' : '0',
    },
    inputArea: {
      background: c.sidebarBg,
      backdropFilter: c.glassEffect,
      borderTop: `1px solid ${c.border}`,
      padding: isMobile ? '16px' : '12px',
      flexShrink: 0,
    },
    inputContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '8px' : '12px',
      background: c.inputBg,
      borderRadius: '28px',
      padding: '8px 12px',
      border: `2px solid ${c.inputBorder}`,
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
    },
    iconButton: {
      padding: '10px',
      color: '#2563eb',
      background: 'transparent',
      border: 'none',
      borderRadius: '50%',
      cursor: 'pointer',
      flexShrink: 0,
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    messageInput: {
      flex: 1,
      padding: '12px 16px',
      background: 'transparent',
      border: 'none',
      outline: 'none',
      fontSize: '15px',
      color: c.text,
      minHeight: '44px',
      resize: 'none',
      fontFamily: 'inherit',
      lineHeight: '1.5',
    },
    sendButton: {
      padding: '12px',
      background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
      color: '#ffffff',
      border: 'none',
      borderRadius: '50%',
      cursor: 'pointer',
      flexShrink: 0,
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
    },
    sendButtonDisabled: {
      background: c.textSecondary,
      cursor: 'not-allowed',
      boxShadow: 'none',
    },
    emptyState: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: c.chatBg,
      padding: '20px',
    },
    emptyStateContent: {
      textAlign: 'center',
      color: c.textSecondary,
    },
    emptyStateIcon: {
      width: '100px',
      height: '100px',
      background: 'linear-gradient(135deg, #3b82f6 0%, #9333ea 100%)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 20px',
      boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
    },
    emptyStateTitle: {
      fontSize: '20px',
      fontWeight: '700',
      color: c.text,
      marginBottom: '8px',
      margin: '0 0 8px 0',
    },
    emptyStateText: {
      color: c.textSecondary,
      margin: 0,
      fontSize: '15px',
    },
    loadingContainer: {
      position: 'relative', // ✅ غيّر لـ relative
      width: '100%',
      height: '100%',
      minHeight: '500px', // ✅ أضف min-height
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: c.sidebarBg,
      backdropFilter: c.glassEffect,
      borderRadius: '20px',
      boxShadow: c.shadow,
    },
    loadingText: {
      textAlign: 'center',
      fontSize: '18px',
      color: c.textSecondary,
    },
    errorContainer: {
      textAlign: 'center',
      color: '#ef4444',
    },
    errorTitle: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '8px',
    },
    errorMessage: {
      fontSize: '14px',
      marginBottom: '16px',
    },
    retryButton: {
      padding: '12px 24px',
      background: '#3b82f6',
      color: '#ffffff',
      border: 'none',
      borderRadius: '12px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
      transition: 'all 0.2s ease',
      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
    },
  });

  const styles = getResponsiveStyles();

  useEffect(() => {
    if (!currentUser?.infinityCode) return;

    const foundSelf = allEmployees.find(emp => isSameUser(emp.id, currentUser.infinityCode));
    if (foundSelf) {
      setAllEmployees(prev => prev.filter(emp => !isSameUser(emp.id, currentUser.infinityCode)));
    }

    const selfConversation = conversations.find(conv => isSameUser(conv.id, currentUser.infinityCode));
    if (selfConversation) {
      setConversations(prev => prev.filter(conv => !isSameUser(conv.id, currentUser.infinityCode)));
    }
  }, [currentUser, allEmployees, conversations]);

  const filteredConversations = conversations.filter(conv =>
    conv.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.lastMessage.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const conversationIds = new Set(conversations.map(c => c.id));

  const filteredEmployees = allEmployees.filter(emp => {
    if (isSameUser(emp.id, currentUser?.infinityCode)) return false;
    if (conversationIds.has(emp.id)) return false;
    return (
      emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.department.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!selectedUser) return;

    // If we intentionally suppressed auto-scroll (e.g., loading older messages), do nothing
    if (suppressAutoScrollRef.current) return;

    // If this is the first load after selecting user, force scroll to bottom
    if (forceScrollOnSelectRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      forceScrollOnSelectRef.current = false;
      return;
    }

    // Only auto-scroll if user is already near bottom
    const isAtBottom = userAtBottomRef.current;
    if (isAtBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, selectedUser]);

  if (loading && conversations.length === 0 && allEmployees.length === 0) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingText}>{t.loading}</div>
      </div>
    );
  }

  if (error && conversations.length === 0 && allEmployees.length === 0) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.errorContainer}>
          <AlertCircle size={48} style={{ marginBottom: '16px', color: '#ef4444' }} />
          <div style={styles.errorTitle}>{t.errorTitle}</div>
          <div style={styles.errorMessage}>{error}</div>
          <button onClick={initializeMessaging} style={styles.retryButton}>
            {t.retry}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={styles.container}>
        <div style={styles.sidebar}>
          <div style={styles.sidebarHeader}>
            <h1 style={styles.sidebarTitle}>
              {t.messages}
              {!isConnected && (
                <span style={{
                  fontSize: '10px',
                  color: '#ef4444',
                  fontWeight: '500',
                  padding: '4px 10px',
                  background: '#fee2e2',
                  borderRadius: '12px'
                }}>
                  Offline
                </span>
              )}
            </h1>

            <div style={styles.searchContainer}>
              <Search style={styles.searchIcon} />
              <input
                type="text"
                placeholder={t.searchPeople}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={styles.searchInput}
              />
            </div>
          </div>

          <div style={styles.usersList}>
            {filteredConversations.length > 0 && (
              <>
                <div style={styles.sectionTitle}>
                  <MessageCircle size={14} />
                  {t.recentChats}
                </div>
                {filteredConversations.map(conv => (
                  <div
                    key={conv.id}
                    onClick={() => handleUserSelect(conv)}
                    style={{
                      ...styles.conversationItem,
                      ...(isSameUser(selectedUser?.id, conv.id) ? styles.conversationItemActive : {})
                    }}
                    onMouseEnter={(e) => {
                      if (!isSameUser(selectedUser?.id, conv.id)) {
                        e.currentTarget.style.background = c.hoverBg;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSameUser(selectedUser?.id, conv.id)) {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={styles.avatarContainer}>
                        {/* ✅ عرض الصورة أو الأحرف */}
{employeeProfilePics[conv.id] ? (
  <img
    src={employeeProfilePics[conv.id]}
    alt={conv.name}
    style={{
      ...styles.avatar,
      objectFit: 'cover'
    }}
    onError={(e) => {
      e.target.style.display = 'none';
      e.target.nextSibling.style.display = 'flex';
    }}
  />
) : null}

<div 
  style={{
    ...styles.avatar,
    background: getAvatarColor(conv.name),
    display: employeeProfilePics[conv.id] ? 'none' : 'flex'
  }}
>
  {conv.avatar}
</div>
                        {conv.online && (
                          <div style={styles.onlineIndicator} />
                        )}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: '4px'
                        }}>
                          <p style={{
  fontWeight: conv.unread > 0 ? '600' : '400',
  color: conv.unread > 0 ? c.text : c.textSecondary,
  margin: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: '15px'
}}>
  {conv.name}
</p>
                          <span style={{
                            fontSize: '12px',
                            color: conv.unread > 0 ? '#2563eb' : c.textSecondary,
                            fontWeight: conv.unread > 0 ? '600' : '400',
                            flexShrink: 0,
                            marginLeft: '8px'
                          }}>
                            {conv.time}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <p style={{
                            fontSize: '14px',
                            color: conv.unread > 0 ? c.text : c.textSecondary,
                            fontWeight: conv.unread > 0 ? '500' : '400',
                            margin: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {conv.lastMessage || t.startConversation}
                          </p>
                          {conv.unread > 0 && (
                            <span style={{
                              marginLeft: '8px',
                              minWidth: '22px',
                              height: '22px',
                              background: '#2563eb',
                              color: '#ffffff',
                              fontSize: '11px',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: '700',
                              flexShrink: 0,
                              padding: '0 6px'
                            }}>
                              {conv.unread}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {filteredEmployees.length > 0 && (
              <>
                <div style={{
                  ...styles.sectionTitle,
                  marginTop: filteredConversations.length > 0 ? '20px' : '0',
                }}>
                  <Users size={14} />
                  {t.allEmployees}
                </div>
                {filteredEmployees.map(emp => (
                  <div
                    key={emp.id}
                    onClick={() => startNewConversation(emp)}
                    style={styles.conversationItem}
                    onMouseEnter={(e) => e.currentTarget.style.background = c.hoverBg}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={styles.avatarContainer}>
                        {employeeProfilePics[emp.id] ? (
                          <img
                            src={employeeProfilePics[emp.id]}
                            alt={emp.name}
                            style={{
                              ...styles.avatar,
                              objectFit: 'cover'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div style={{
                          ...styles.avatar,
                          background: getAvatarColor(emp.name),
                          display: employeeProfilePics[emp.id] ? 'none' : 'flex'
                        }}>
                          {emp.avatar}
                        </div>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: '15px',
                          fontWeight: '500',
                          color: c.text,
                          marginBottom: '4px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {getTrilateralName(emp.name)}
                        </div>
                        <div style={{
                          fontSize: '13px',
                          color: c.textSecondary,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {emp.department || emp.position || emp.email}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {filteredConversations.length === 0 && filteredEmployees.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: c.textSecondary }}>
                <Users size={56} style={{ marginBottom: '16px', opacity: 0.4 }} />
                <p style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: c.text }}>
                  {searchQuery ? t.noResults : t.noEmployees}
                </p>
                {searchQuery && (
                  <p style={{ fontSize: '14px', color: c.textSecondary }}>{t.tryDifferent}</p>
                )}
              </div>
            )}
          </div>
        </div>

        <div style={styles.chatArea}>
          {selectedUser ? (
            <>
              <div style={styles.chatHeader}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button onClick={() => setSelectedUser(null)} style={styles.backButton}>
                      <ChevronLeft style={{ width: '22px', height: '22px' }} />
                    </button>

                    <div style={{ position: 'relative' }}>
                      {/* ✅ صورة المستخدم في الـ header */}
{employeeProfilePics[selectedUser.id] ? (
  <img
    src={employeeProfilePics[selectedUser.id]}
    alt={selectedUser.name}
    style={{
      ...styles.avatar,
      width: '48px',
      height: '48px',
      objectFit: 'cover'
    }}
    onError={(e) => {
      e.target.style.display = 'none';
      e.target.nextSibling.style.display = 'flex';
    }}
  />
) : null}

<div 
  style={{
    ...styles.avatar,
    width: '48px',
    height: '48px',
    fontSize: '18px',
    background: getAvatarColor(selectedUser.name),
    display: employeeProfilePics[selectedUser.id] ? 'none' : 'flex'
  }}
>
  {selectedUser.avatar}
</div>
                      {selectedUser.online && (
                        <div style={styles.onlineIndicator} />
                      )}
                    </div>

                    <div>
                      <h2 style={{
                        fontWeight: '600',
                        color: c.text,
                        margin: 0,
                        fontSize: '16px'
                      }}>
                        {selectedUser.name}
                      </h2>
                      <p style={{
                        fontSize: '13px',
                        color: c.textSecondary,
                        margin: 0
                      }}>
                        {typingUsers.has(selectedUser.id) ? (
                          <span style={{ fontStyle: 'italic', color: '#2563eb' }}>{t.typing}</span>
                        ) : (
                          selectedUser.online ? t.activeNow : t.offline
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

                <div ref={messagesContainerRef} onScroll={() => {
                  const c = messagesContainerRef.current;
                  if (!c) return;
                  const nearBottom = (c.scrollHeight - c.scrollTop - c.clientHeight) < 120;
                  userAtBottomRef.current = nearBottom;
                }} style={styles.messagesContainer}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  padding: '8px'
                }}>
                 {hasMoreMessages[selectedUser.id] && (
  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
    <button 
      onClick={() => loadMoreMessages(selectedUser.id)} 
      disabled={loadingMoreFor === selectedUser.id} 
      style={{ 
        padding: '10px 20px', 
        borderRadius: '12px', 
        border: `1px solid ${c.inputBorder}`, 
        background: loadingMoreFor === selectedUser.id ? c.textSecondary : c.inputBg, 
        color: c.text,
        cursor: loadingMoreFor === selectedUser.id ? 'not-allowed' : 'pointer',
        fontWeight: '600',
        fontSize: '14px',
        transition: 'all 0.2s ease',
      }}
    >
      {loadingMoreFor === selectedUser.id ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '14px',
            height: '14px',
            border: '2px solid currentColor',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }} />
          Loading...
        </div>
      ) : (
        `Load more messages (${messagesOffset[selectedUser.id] || 0} loaded)`
      )}
    </button>
  </div>
)}
                  {(messages[selectedUser.id] || []).map((message, index) => {
                    const showAvatar = index === 0 ||
                      messages[selectedUser.id][index - 1]?.sent !== message.sent;

                    return (
                      <div
                        key={message.id}
                        style={{
                          ...styles.messageWrapper,
                          justifyContent: message.sent ?
                            (isRTL ? 'flex-start' : 'flex-end') :
                            (isRTL ? 'flex-end' : 'flex-start'),
                          paddingLeft: message.sent ? '60px' : '0',
                          paddingRight: message.sent ? '0' : '60px',
                        }}
                      >
{!message.sent && showAvatar && (
  <>
    {/* ✅ صورة المرسل في الرسائل */}
    {employeeProfilePics[selectedUser.id] ? (
      <img
        src={employeeProfilePics[selectedUser.id]}
        alt={selectedUser.name}
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          objectFit: 'cover',
          flexShrink: 0,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          border: `2px solid ${c.chatBg}`,
        }}
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.nextSibling.style.display = 'flex';
        }}
      />
    ) : null}

    <div 
      style={{
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        display: employeeProfilePics[selectedUser.id] ? 'none' : 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: '600',
        flexShrink: 0,
        background: getAvatarColor(selectedUser.name),
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        border: `2px solid ${c.chatBg}`,
      }}
    >
      {selectedUser.avatar}
    </div>
  </>
)}

 
                        {!message.sent && !showAvatar && (
                          <div style={{ width: '36px', flexShrink: 0 }} />
                        )}

                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: message.sent ? 'flex-end' : 'flex-start',
                          flex: 1,
                          maxWidth: '100%',
                        }}>
                          <div style={{
                            ...styles.messageBubble,
                            ...(message.sent ? styles.myMessage : styles.theirMessage),
                          }}>
                            {renderMessageContent(message)}
                          </div>

                          {showAvatar && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              marginTop: '6px',
                              paddingLeft: message.sent ? '0' : '12px',
                              paddingRight: message.sent ? '12px' : '0',
                            }}>
                              <p style={{
                                fontSize: '11px',
                                color: c.textSecondary,
                                margin: 0,
                              }}>
                                {message.time}
                              </p>
                              {message.sent && message.status === 'sending' && (
                                <span style={{
                                  fontSize: '10px',
                                  color: c.textSecondary,
                                  fontStyle: 'italic'
                                }}>
                                  • {t.sending}
                                </span>
                              )}
                              {message.sent && message.status === 'sent' && (
                                <span style={{ fontSize: '12px', color: c.textSecondary }}>✓</span>
                              )}
                              {message.sent && message.status === 'read' && (
                                <span style={{ fontSize: '12px', color: '#2563eb' }}>✓✓</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              <div style={styles.inputArea}>
                {(isRecording || audioBlob) && (
                  <div style={{
                    padding: '16px',
                    background: c.inputBg,
                    borderRadius: '12px',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}>
                    {isRecording ? (
                      <>
                        <div style={{
                          width: '12px',
                          height: '12px',
                          background: '#ef4444',
                          borderRadius: '50%',
                          animation: 'pulse 1s infinite',
                        }} />
                        <span style={{ flex: 1, fontSize: '14px', color: c.text }}>
                          {t.recording} {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                        </span>
                        <button onClick={stopRecording} style={{
                          padding: '8px 16px',
                          background: '#ef4444',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '600',
                        }}>
                          {t.stop}
                        </button>
                      </>
                    ) : (
                      <>
                        <Mic size={20} color={c.text} />
                        <span style={{ flex: 1, fontSize: '14px', color: c.text }}>
                          {t.voiceMessage} ({recordingTime}s)
                        </span>
                        <button onClick={sendRecording} style={{
                          padding: '8px 16px',
                          background: '#22c55e',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '600',
                        }}>
                          {t.send}
                        </button>
                        <button onClick={cancelRecording} style={{
                          padding: '8px 16px',
                          background: '#ef4444',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '600',
                        }}>
                          {t.cancel}
                        </button>
                      </>
                    )}
                  </div>
                )}

                {uploading && (
                  <div style={{
                    padding: '8px 16px',
                    background: c.inputBg,
                    borderRadius: '12px',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #2563eb',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                    }} />
                    <span style={{ fontSize: '14px', color: c.text }}>
                      {t.uploading}
                    </span>
                  </div>
                )}

                <div style={styles.inputContainer}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf,.pdf,application/vnd.ms-excel,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx,application/msword,.doc,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx,application/vnd.ms-powerpoint,.ppt,application/vnd.openxmlformats-officedocument.presentationml.presentation,.pptx"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  <button
                    style={styles.iconButton}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || isRecording || audioBlob}
                  >
                    <Paperclip style={{ width: '20px', height: '20px' }} />
                  </button>

                  <button
                    style={{
                      ...styles.iconButton,
                      color: isRecording ? '#ef4444' : '#2563eb'
                    }}
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={uploading || audioBlob}
                  >
                    <Mic style={{ width: '20px', height: '20px' }} />
                  </button>

                  <textarea
                    value={messageInput}
                    onChange={(e) => {
                      setMessageInput(e.target.value);
                      handleTyping();
                    }}
                    onKeyPress={handleKeyPress}
                    placeholder={t.typeMessage}
                    disabled={!isConnected || isRecording || audioBlob}
                    style={styles.messageInput}
                    rows="1"
                  />

                  {/* {!isMobile && (
                    <button style={styles.iconButton} disabled={isRecording || audioBlob}>
                      <Smile style={{ width: '20px', height: '20px' }} />
                    </button>
                  )} */}

                  <button
                    onClick={handleSendMessage}
                    disabled={!messageInput.trim() || !isConnected || isRecording || audioBlob}
                    style={{
                      ...styles.sendButton,
                      ...((!messageInput.trim() || !isConnected || isRecording || audioBlob) && styles.sendButtonDisabled)
                    }}
                  >
                    <Send style={{ width: '20px', height: '20px' }} />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div style={styles.emptyState}>
              <div style={styles.emptyStateContent}>
                <div style={styles.emptyStateIcon}>
                  <Send style={{ width: '50px', height: '50px', color: '#ffffff' }} />
                </div>
                <h3 style={styles.emptyStateTitle}>
                  {t.yourMessages}
                </h3>
                <p style={styles.emptyStateText}>
                  {t.selectPerson}
                </p>
              </div>
            </div>
          )}
        </div>

        <style>{`
          @media (min-width: 768px) {
            .md-sidebar {
              display: flex !important;
              width: 380px !important;
              min-width: 380px;
            }
            .md-chat {
              display: flex !important;
            }
            .mobile-back {
              display: none !important;
            }
          }
          @media (max-width: 767px) {
            .hide-mobile {
              display: none !important;
            }
          }

          button:hover:not(:disabled) {
            background: ${c.hoverBg} !important;
            transform: scale(1.08);
          }

          button:active:not(:disabled) {
            transform: scale(0.95);
          }

          input:focus, textarea:focus {
            border-color: #2563eb !important;
          }

          * {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          
          *::-webkit-scrollbar {
            display: none;
          }

          * {
            transition: background 0.2s ease, border-color 0.2s ease, transform 0.15s ease;
          }

          textarea {
            resize: none;
            font-family: inherit;
          }

          @keyframes spin {
            to { transform: rotate(360deg); }
          }

          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </div>
    </>
  );
};

export default MessengerPage;